// redux/slices/whatsappSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { headers as baseHeaders } from "../apiConfig";
import { getServerUrl } from "../../Utils/serverSelector";
import { resetStore } from "./resetStore";
import { getJwtToken } from "../../Utils/authStorage";

/* ===================== HELPERS ===================== */

const axiosRequest = async ({ url, method = "POST", data }) => {
  const fullUrl = `${getServerUrl()}${url}`;
  const jwt = await getJwtToken();

  const headers = {
    ...(baseHeaders || {}),
    "Content-Type": "application/json",
    ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
  }

  try {
    const res = await axios({
      url: fullUrl,
      method,
      headers,
      withCredentials: true,
      data,
    });

    return res;
  } catch (error) {

    throw error;
  }
};
/* ===================== THUNKS ===================== */

export const sendWhatsappMessage = createAsyncThunk(
  "whatsapp/sendWhatsappMessage",
  async ({ phone, message, ...rest }, { rejectWithValue }) => {
    try {
      if (!phone) throw new Error("Номер телефона не передан");
      if (!message) throw new Error("Текст сообщения не передан");

      const { data } = await axiosRequest({
        url: "/send-message",
        method: "POST",
        data: { phone, message, ...rest },
      });

      return data;
    } catch (error) {
      const errorValue =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        "Failed to send WhatsApp message";


      return rejectWithValue(errorValue);
    }
  }
);

export const sendWhatsAppTemplate = createAsyncThunk(
  "whatsapp/sendTemplateWeb",
  async (
    {
      phone,
      templateName,
      lang = "ru",
      params = [],
      withButtons = false,
      sendID,
      firstName,
      text,
      uid,
    },
    { rejectWithValue }
  ) => {
    try {
      const payload = {
        phone,
        templateName,
        lang,
        params,
        withButtons,
        sendID,
        firstName,
        text,
        uid,
      };


      const { data } = await axiosRequest({
        url: "/whatsapp-messages/send-template",
        method: "POST",
        data: payload,
      });

      return data;
    } catch (error) {
      const errorValue =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        "Failed to send WhatsApp template";

      console.log("[WA SEND TEMPLATE REJECT]", errorValue);

      return rejectWithValue(errorValue);
    }
  }
);

/* ===================== STATE ===================== */

const initialState = {
  status: "idle",
  error: null,
  lastResponse: null,
  sentMessages: [],
  connected: false,
};

/* ===================== SLICE ===================== */

const whatsappSlice = createSlice({
  name: "whatsapp",
  initialState,
  reducers: {
    clearWhatsappError: (state) => {
      state.error = null;
    },
    clearWhatsappLastResponse: (state) => {
      state.lastResponse = null;
    },
    clearWhatsappMessages: (state) => {
      state.sentMessages = [];
      state.status = "idle";
      state.error = null;
      state.lastResponse = null;
    },
    resetWhatsappStatus: (state) => {
      state.status = "idle";
      state.error = null;
    },
    setWhatsappConnected: (state, action) => {
      state.connected = !!action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendWhatsappMessage.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(sendWhatsappMessage.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.lastResponse = action.payload || null;
        if (action.payload) state.sentMessages.push(action.payload);
      })
      .addCase(sendWhatsappMessage.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to send WhatsApp message";
      })

      .addCase(sendWhatsAppTemplate.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(sendWhatsAppTemplate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.lastResponse = action.payload || null;
        if (action.payload) state.sentMessages.push(action.payload);
      })
      .addCase(sendWhatsAppTemplate.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to send WhatsApp template";
      })

      .addCase(resetStore, () => initialState);
  },
});

/* ===================== ACTIONS ===================== */

export const {
  clearWhatsappError,
  clearWhatsappLastResponse,
  clearWhatsappMessages,
  resetWhatsappStatus,
  setWhatsappConnected,
} = whatsappSlice.actions;

/* ===================== SELECTORS ===================== */

export const selectWhatsappState = (state) => state.whatsapp;

export const selectWhatsappStatus = (state) =>
  state.whatsapp?.status || "idle";

export const selectWhatsappError = (state) =>
  state.whatsapp?.error || null;

export const selectWhatsappLastResponse = (state) =>
  state.whatsapp?.lastResponse || null;

export const selectWhatsappMessages = (state) =>
  state.whatsapp?.sentMessages || [];

export const selectWhatsappConnected = (state) =>
  !!state.whatsapp?.connected;

export default whatsappSlice.reducer;